import React from 'react';
import Table, { TableColumn } from '../../components/common/Table';
import { RangeDTO } from '../../services/apiClient';
import { convertToUserTime } from '../../utils/dateUtils/convertToUserTime';
import { renderBooleanBadge, getBadgeColors } from '../../utils/displayUtils';
import { Badge } from '../common/Badge';
import FileIcons from '../common/FileIcons';
import createRangeActions from '../actionMenus/RangeActions';
import ReviewPanel from '../reviewPanels/ReviewPanel';

interface RangeTableProps {
  data: RangeDTO[];
  handleEdit: (row: RangeDTO) => void;
  handleDelete: (row: RangeDTO) => void;
  handleSendEmail: (row: RangeDTO) => void;
  handleViewAllEmails: (row: RangeDTO) => void;
  handleViewRecordEmails: (row: RangeDTO) => void;
  handleApprove: (row: RangeDTO) => void;
  handleChangeStatus: (row: RangeDTO) => void;
  currentPage: number;
  totalPages: number;
  setCurrentPage: (page: number) => void;
  pageSize: number;
  setPageSize: (size: number) => void;
  isLoading: boolean;
  sortBy?: string;
  sortDescending?: boolean;
  onSort?: (sortKey: string) => void;
}

const RangeTable: React.FC<RangeTableProps> = ({
  data,
  handleEdit,
  handleDelete,
  handleSendEmail,
  handleViewAllEmails,
  handleViewRecordEmails,
  handleApprove,
  handleChangeStatus,
  currentPage,
  totalPages,
  setCurrentPage,
  pageSize,
  setPageSize,
  isLoading,
  sortBy,
  sortDescending,
  onSort,
}) => {
  const columns: TableColumn[] = [
    {
      label: 'Type',
      key: 'rangeTypeId',
      showInReview: true,
      render: (row: RangeDTO) =>
        <span>{row.rangeType?.title ?? 'Unknown'}</span>,
    },
    {
      label: 'Friendly',
      key: 'isFriendly',
      showInReview: true,
      render: (row: RangeDTO) =>
        renderBooleanBadge(row.isFriendly ?? false, 'Yes', 'No'),
    },
    {
      label: 'Name',
      key: 'title',
      sortable: true,
      sortKey: 'Title',
      showInReview: true,
    },
    {
      label: 'Town',
      key: 'closestCity',
      sortable: true,
      sortKey: 'ClosestCity',
      showInReview: true,
    },
    {
      label: 'Responsible',
      key: 'applicantFirstName',
      sortable: true,
      sortKey: 'ApplicantFirstName',
      showInReview: true,
      render: (row: RangeDTO) =>
        <span>{row.applicantFirstName ?? ''} {row.applicantLastName ?? ''}</span>,
    },
    {
      label: 'Files',
      key: 'files',
      render: (row: RangeDTO) => (
        <FileIcons
          files={[
            { id: row.shootingPointImageId, label: 'Shooting Point' },
            { id: row.backStopImageId, label: 'Back Stop' },
            { id: row.googleMapsImageId, label: 'Google Maps' },
            { id: row.otherImageId, label: 'Other' }
          ]}
          renderSidePanel={() => <ReviewPanel row={row} columns={columns} />}
        />
      ),
    },
    {
      label: 'Status',
      key: 'rangeStatusId',
      sortable: true,
      sortKey: 'RangeStatusTitle',
      showInReview: true,
      render: (row: RangeDTO) => {
        const base = row.rangeStatus?.colourGrading?.colour ?? '#ffffffff';
        const { bg, text } = getBadgeColors(base, 0.18, 0.40);
        return base ? (
          <Badge
            label={row.rangeStatus?.title || 'Unknown'}
            variant="custom"
            customBgColor={bg}
            customTextColor={text}
          />
        ) : (
          <span>{row.rangeStatus?.title || 'Unknown'}</span>
        );
      },
    },
    {
      label: 'Added',
      key: 'createdUtc',
      sortable: true,
      sortKey: 'CreatedUtc',
      format: (value: Date) => convertToUserTime(value?.toString() ?? ''),
    },
  ];

  return (
    <Table
      columns={columns}
      data={data}
      actions={{
        onEdit: handleEdit,
        extra: (row) =>
          createRangeActions({
            onView: handleEdit,
            onDelete: handleDelete,
            onSendEmail: handleSendEmail,
            onViewAllEmails: handleViewAllEmails,
            onViewRecordEmails: handleViewRecordEmails,
            onApprove: handleApprove,
            onChangeStatus: handleChangeStatus,
          })(row),
      }}
      currentPage={currentPage}
      totalPages={totalPages}
      onPageChange={setCurrentPage}
      pageSize={pageSize}
      onPageSizeChange={setPageSize}
      isLoading={isLoading}
      sortBy={sortBy}
      sortDescending={sortDescending}
      onSort={onSort}
    />
  );
};

export default RangeTable;
